/**
 * Операции кошелька из источника данных (docs/08-screens.md): переводы
 * в блокчейне, пересланные сообщения. Оплата ведёт в сервис, обмен
 * и поступление в траты не входят. Операция, ждущая решения владельца,
 * показывается без суммы и получателя: она может оказаться личной.
 */
import { Card } from '@/ds';
import { Money, Provenance } from '@/components/money';
import type { WalletDetail, WalletOperationRow } from '@/api/hooks';
import { formatDateTime } from '@/lib/format';
import { useIsMobile } from '@/lib/useMediaQuery';

const KIND_LABELS: Record<string, string> = {
  payment: 'Оплата сервиса',
  topup: 'Поступление',
  swap: 'Обмен внутри кошелька',
  expense: 'Прочий расход',
  unknown: 'Ждёт решения владельца',
};

function whatText(row: WalletOperationRow): string {
  if (row.kind === 'payment') {
    return row.serviceName ?? row.problem ?? 'Сервис не определён';
  }
  if (row.kind === 'expense') return row.expenseComment ?? 'Рабочая трата';
  if (row.kind === 'swap') return `${row.asset}, в траты не входит`;
  if (row.kind === 'topup') return row.counterparty ? `с ${row.counterparty}` : row.asset;
  if (row.kind === 'unknown') return 'Сумма и получатель видны только владельцу в Telegram';
  return row.asset;
}

export function WalletOperationsCard({ wallet }: { wallet: WalletDetail }) {
  const isMobile = useIsMobile();

  return (
    <Card padding={isMobile ? 'sm' : 'md'}>
      <div
        style={{
          font: '500 16px var(--font-display)',
          letterSpacing: 'var(--ls-heading)',
          color: 'var(--text-strong)',
          marginBottom: 'var(--space-2)',
        }}
      >
        Операции из источника
      </div>
      <div style={{ marginBottom: 'var(--space-5)' }}>
        <Provenance>
          Оплаты известным получателям сами становятся оплатами сервисов. Обмены и поступления в
          траты не входят.
        </Provenance>
      </div>

      <div className="kj-scroll-x">
        <table className="kj-table">
          <thead>
            <tr>
              <th>Когда</th>
              <th>Что</th>
              <th>Сумма</th>
              <th>Подробности</th>
            </tr>
          </thead>
          <tbody>
            {wallet.operations.map((row) => (
              <tr key={row.id}>
                <td data-label="Когда" className="kj-mono">
                  {formatDateTime(row.occurredAt)}
                </td>
                <td
                  data-label="Что"
                  style={{
                    color: row.kind === 'unknown' ? 'var(--status-warning-fg)' : 'var(--text-strong)',
                  }}
                >
                  {KIND_LABELS[row.kind] ?? row.kind}
                </td>
                <td data-label="Сумма">
                  {row.amount === null ? (
                    <span style={{ color: 'var(--text-subtle)' }}>—</span>
                  ) : (
                    <Money
                      minor={row.amount}
                      currency={wallet.currency}
                      size="sm"
                      tone={row.kind === 'swap' || row.kind === 'topup' ? 'muted' : 'normal'}
                    />
                  )}
                </td>
                <td data-label="Подробности" style={{ color: 'var(--text-muted)' }}>
                  {whatText(row)}
                  {row.kind === 'payment' && row.counterparty ? (
                    <Provenance>{`на ${row.counterparty}`}</Provenance>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
